import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TASKPILOT_BACKEND_URL, TASKPILOT_WEBSOCKET_URL } from '@data-terminal/data-access/injection-tokens';
import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';
import { HdmuiMainApplicationHeaderTaskPilotItem, HdmuiTaskPilotProvider } from '@heidelberg/hdmui-angular';
import { TranslateService } from '@ngx-translate/core';
import {
    catchError,
    combineLatest,
    defer,
    distinctUntilChanged,
    expand,
    filter,
    map,
    merge,
    Observable,
    of,
    retry,
    startWith,
    switchMap,
    timer,
} from 'rxjs';
import { webSocket } from 'rxjs/webSocket';

import {
    PollingTaskPilotItem,
    TaskPilotAppInfoResponse,
    TaskPilotWebsocketMessage,
} from './provide-task-pilot-provider.model';

const TASK_PILOT_API_SUFFIX_PATTERN = /\/api\/v1\/?$/;
const TRAILING_SLASH_PATTERN = /\/+$/;
const TASK_PILOT_WHITELIST_STATUS_PATH = '/public/whitelist/task-form/status';
const DEFAULT_BADGE_VALUE = 0;
const DEFAULT_REFRESH_AFTER_SECONDS = 120;
const DEFAULT_LOCALE = 'en';
const WEBSOCKET_RECONNECT_INITIAL_DELAY_MS = 5000;
const WEBSOCKET_RECONNECT_MAX_DELAY_MS = 60_000;
const USER_TASK_COUNT_TOPIC = 'UserTaskCount';

export function provideTaskPilotProvider(): HdmuiTaskPilotProvider {
    const http = inject(HttpClient);
    const translateService = inject(TranslateService);
    const ccAuthService = inject(CCAuthService);
    const taskPilotBackendUrl = inject(TASKPILOT_BACKEND_URL);
    const taskPilotWebsocketUrl = inject(TASKPILOT_WEBSOCKET_URL);
    const taskPilotApiUrl = taskPilotBackendUrl.replace(TRAILING_SLASH_PATTERN, '');
    const fallbackTaskPilotLink = taskPilotBackendUrl.replace(TASK_PILOT_API_SUFFIX_PATTERN, '');
    const localeChanges$ = merge(
        of(getLocale(translateService)),
        translateService.onLangChange.pipe(map(({ lang }) => lang))
    ).pipe(distinctUntilChanged());

    return {
        taskPilot: toSignal(
            checkWhitelistAndGetAppInfo(
                http,
                taskPilotApiUrl,
                localeChanges$,
                fallbackTaskPilotLink,
                taskPilotWebsocketUrl,
                ccAuthService
            ),
            {
                initialValue: undefined,
            }
        ),
    };
}

function pollTaskPilotAppInfo(
    http: HttpClient,
    taskPilotApiUrl: string,
    locale: string,
    fallbackTaskPilotLink: string
): Observable<HdmuiMainApplicationHeaderTaskPilotItem> {
    return createTaskPilotAppInfoRequest(http, taskPilotApiUrl, locale, fallbackTaskPilotLink).pipe(
        expand((response) =>
            timer(response.refreshAfterMs).pipe(
                switchMap(() => createTaskPilotAppInfoRequest(http, taskPilotApiUrl, locale, fallbackTaskPilotLink))
            )
        ),
        map((response) => response.item)
    );
}

function checkWhitelistAndGetAppInfo(
    http: HttpClient,
    taskPilotApiUrl: string,
    localeChanges$: Observable<string>,
    fallbackTaskPilotLink: string,
    taskPilotWebsocketUrl: string,
    ccAuthService: CCAuthService
): Observable<HdmuiMainApplicationHeaderTaskPilotItem | undefined> {
    return getWhitelistStatus(http, taskPilotApiUrl).pipe(
        switchMap((isWhitelisted) => {
            if (!isWhitelisted) {
                return of(undefined);
            }
            const polled$ = localeChanges$.pipe(
                switchMap((locale) => pollTaskPilotAppInfo(http, taskPilotApiUrl, locale, fallbackTaskPilotLink))
            );
            const wsCount$ = createUserTaskCountStream(taskPilotWebsocketUrl, ccAuthService).pipe(
                // The WS overlay is optional; if it ever errors past retries, keep polling alive.
                catchError(() => of<number | undefined>(undefined)),
                startWith<number | undefined>(undefined)
            );
            return combineLatest([polled$, wsCount$]).pipe(
                map(([polledItem, liveCount]) => ({
                    ...polledItem,
                    tasksCount: liveCount ?? polledItem.tasksCount,
                }))
            );
        })
    );
}

function createTaskPilotAppInfoRequest(
    http: HttpClient,
    taskPilotApiUrl: string,
    locale: string,
    fallbackTaskPilotLink: string
): Observable<PollingTaskPilotItem> {
    return defer(() =>
        http
            .get<TaskPilotAppInfoResponse>(`${taskPilotApiUrl}/appinfo`, {
                params: { locale },
            })
            .pipe(
                map((response) => ({
                    item: mapTaskPilotItem(response, fallbackTaskPilotLink),
                    refreshAfterMs: getRefreshAfterMs(response),
                })),
                catchError(() => {
                    return of({
                        item: createFallbackTaskPilotItem(fallbackTaskPilotLink),
                        refreshAfterMs: DEFAULT_REFRESH_AFTER_SECONDS * 1000,
                    });
                })
            )
    );
}

function getWhitelistStatus(http: HttpClient, taskPilotApiUrl: string): Observable<boolean> {
    return http.get<boolean>(`${taskPilotApiUrl}${TASK_PILOT_WHITELIST_STATUS_PATH}`).pipe(
        catchError(() => {
            return of(false);
        })
    );
}

function mapTaskPilotItem(
    response: TaskPilotAppInfoResponse,
    fallbackTaskPilotLink: string
): HdmuiMainApplicationHeaderTaskPilotItem {
    const appIcon = response.appIcons?.[0];

    return {
        tasksCount: appIcon?.badge?.value ?? DEFAULT_BADGE_VALUE,
        taskPilotLink: appIcon?.launchUrl ?? response.launchUrl ?? fallbackTaskPilotLink,
    };
}

function createFallbackTaskPilotItem(fallbackTaskPilotLink: string): HdmuiMainApplicationHeaderTaskPilotItem {
    return {
        tasksCount: DEFAULT_BADGE_VALUE,
        taskPilotLink: fallbackTaskPilotLink,
    };
}

function getRefreshAfterMs(response: TaskPilotAppInfoResponse): number {
    const refreshAfterSeconds = response.appIcons?.[0]?.badge?.refreshAfter;

    return (
        (refreshAfterSeconds && refreshAfterSeconds > 0 ? refreshAfterSeconds : DEFAULT_REFRESH_AFTER_SECONDS) * 1000
    );
}

function getLocale(translateService: TranslateService): string {
    return translateService.getCurrentLang() ?? translateService.getFallbackLang() ?? DEFAULT_LOCALE;
}

function createUserTaskCountStream(taskPilotWebsocketUrl: string, ccAuthService: CCAuthService): Observable<number> {
    if (!taskPilotWebsocketUrl) {
        return of();
    }
    return defer(() =>
        webSocket<TaskPilotWebsocketMessage>({
            url: taskPilotWebsocketUrl,
            protocol: ['Authorization', ccAuthService.getPersonToken()],
        })
    ).pipe(
        map(extractUserTaskCount),
        filter((count): count is number => count !== undefined),
        // Reconnect with exponential backoff capped at WEBSOCKET_RECONNECT_MAX_DELAY_MS;
        // resetOnSuccess so a long-lived connection that drops occasionally starts fresh each time.
        retry({
            delay: (_error, retryCount) =>
                timer(
                    Math.min(
                        WEBSOCKET_RECONNECT_INITIAL_DELAY_MS * 2 ** (retryCount - 1),
                        WEBSOCKET_RECONNECT_MAX_DELAY_MS
                    )
                ),
            resetOnSuccess: true,
        })
    );
}

function extractUserTaskCount(message: TaskPilotWebsocketMessage): number | undefined {
    if (message?.topic !== USER_TASK_COUNT_TOPIC) {
        return undefined;
    }
    const count = message.payload?.count;
    return typeof count === 'number' ? count : undefined;
}
